import React, { useEffect, useState } from "react";
import { Tabs, Tab, Box, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";

// Styling for the tabs container
const GradientTabs = styled(Tabs)({
  overflow: "hidden",
  "& .MuiTabs-indicator": {
    display: "none", // This will remove the indicator completely
  },
});

interface GradientTabProps {
  color: string; // Color for tab background
  isFirstTab: boolean;
  isLastTab?: boolean;
  tabStatus: "before" | "selected" | "after"; // Tab position relative to selected
  disabled: boolean; // Whether tab is disabled
  statusLabel?: string; // Optional label for status (e.g., "New Lead")
}

// Styled component for individual tabs
const GradientTab = styled(Tab)<GradientTabProps>(
  ({ color, isFirstTab, tabStatus, statusLabel, disabled, isLastTab }) => {
    const isSurveyedTab = statusLabel?.toUpperCase() === "SURVEYED";
    const isCompletedTab = statusLabel?.toUpperCase() === "COMPLETED";
    const isProvisionalTab = statusLabel?.toUpperCase() ==="PROVISIONAL"
    const useBlackStatusText = isSurveyedTab || isCompletedTab || isProvisionalTab;

    return {
    textTransform: "none",
    color: disabled
      ? "#7f7f7f"
      : tabStatus === "after"
        ? "#1F1F1F"
        : isLastTab && !isCompletedTab
          ? "#ffffff"
          : useBlackStatusText
            ? "#000000"
            : "#ffffff",
    fontWeight: 600,
    fontSize: "13px",
    width: "108px",
    padding: "0px 24px !important",
    paddingLeft: isFirstTab ? "none" : "40px !important",
    textAlign: "center",
    backgroundColor: disabled
      ? "#e9e9e9"
      : tabStatus === "selected"
        ? "blue"
        : tabStatus === "before"
          ? color
          : "#e9e9e9",
    opacity: 1,
    clipPath: isFirstTab
      ? "polygon(0% 0%, 75% 0%, 100% 50%, 75% 100%, 0% 100%)"
      : "polygon(75% 0%, 100% 50%, 75% 100%, 0% 100%, 25% 50%, 0% 0%)",
    transition: "transform 0.3s ease-in-out",
    marginLeft: isFirstTab ? "0px" : "-20px",
    "&.Mui-selected": {
      color: disabled ? "#7f7f7f" : useBlackStatusText ? "#000000" : "#ffffff",
      fontWeight: "bold",
      opacity: 1,
      backgroundColor: disabled ? "#e9e9e9" : color,
      border: "0px solid",
    },
    "&.Mui-selected:last-child": {
      color: isCompletedTab ? "#000000" : "#ffffff",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "rgba(100, 95, 228, 0.32)",
    },
  };
});

interface RemovalTabProps {
  leadDetails: any;
}

const RemovalTab: React.FC<RemovalTabProps> = ({ leadDetails }) => {
  const status = leadDetails?.lead?.status;
  const normalizedStatus = status?.toUpperCase(); // normalize to uppercase
  const hasStatus = !!status;
  const showStorageEnq =
    normalizedStatus === "STORAGE ENQ" || normalizedStatus === "STORAGE_ENQ";
  const showStorage = normalizedStatus === "STORAGE";
  const isStorageFlow = showStorageEnq || showStorage;
  const isDebtorFlow = normalizedStatus === "DEBTOR";
  const isExternalFlow = normalizedStatus === "EXTERNAL";
  const showArchived = normalizedStatus === "ARCHIVED";
  const showProvisional = normalizedStatus === "PROVISIONAL";
  const showAwaiting = normalizedStatus ==="AWAITING";
  const showDispute = normalizedStatus ==="DISPUTE";
  const showLost = normalizedStatus ==='LOST';

  // Map statuses to indices, handling both ESTIMATE and ESTIMATES
  const statusToIndex: { [key: string]: number } = {
    NEW: 0,
    ESTIMATE: 1,
    ESTIMATES: 1, // both map to index 1
    SURVEY: 2,
    QUOTE: 3,
    CONFIRMED: 4,
    JOB: 5,
    "STORAGE ENQ": isStorageFlow ? 0 : -1,
    STORAGE_ENQ: isStorageFlow ? 0 : -1,
    STORAGE: isStorageFlow ? 1 : -1,
    DEBTOR: isDebtorFlow ? 0 : -1,
    EXTERNAL: isExternalFlow ? 0 : -1,
    ARCHIVED: showArchived ? 6 : -1,
    AWAITING: showAwaiting ? 6 : -1,
    LOST: showLost ? 6 : -1,
    DISPUTE: showDispute ? 6 : -1,
    COMPLETED: (showArchived || showAwaiting || showLost ) ? 7 : 6,
  };

  // Setup initial tab value from status
  const [value, setValue] = useState(statusToIndex[normalizedStatus] ?? 0);

  useEffect(() => {
    setValue(statusToIndex[normalizedStatus] ?? 0);
  }, [normalizedStatus]);


  // Define tab labels - use singular "Estimate"
  let tabLabels = [
    "New Lead",
    "Estimate",
    leadDetails?.lead?.survey_status === "COMPLETED" ? "Surveyed" : "Survey",
    "Quote",
    "Confirmed",
    "Job",
    "Completed",
  ];

  

  let tabColors = [
    "#FF66C4", // NEW LEAD COLOR
    "#D16B2C", // ESTIMATE COLOR
    leadDetails?.lead?.survey_status === "COMPLETED" ? "#00FFFF" : "#7ED957", // SURVEY COLOR
    "#FF3131", // QUOTE COLOR
    "#7C3014", // CONFIRMED COLOR
    "#a52a2a", // JOB COLOR
    "#FFF7D2", // COMPLETED COLOR
  ];

  // If status is STORAGE flow, show only Storage Enq and Storage tabs
  if (isStorageFlow) {
    tabLabels = ["Storage Enq", "Storage"];
    tabColors = ["#5500AA", "#6aa84f"];
  }

  if(isDebtorFlow){
    tabLabels = ["Debtor"];
    tabColors = ["#FF66C4"];
  }

  if(isExternalFlow){
    tabLabels = ["External"];
    tabColors = ["#FF66C4"];
  }

  // Conditionally insert PROVISIONAL after CONFIRMED if lead status is PROVISIONAL
  if (showProvisional) {
    const index = tabLabels.indexOf("Quote");
    if (index !== -1) {
      tabLabels.splice(index + 1, 0, "Provisional");
      tabColors.splice(index + 1, 0, "#FFFF00");
    }
  }

  // Insert Archived tab before Completed if status is ARCHIVED
  if (showArchived) {
    const completedIndex = tabLabels.indexOf("Completed");
    if (completedIndex !== -1) {
      tabLabels.splice(completedIndex, 0, "Archived");
      tabColors.splice(completedIndex, 0, "#666666");
    }
  }

  // Insert Awaiting tab before completed if status is AWAITING
  if(showAwaiting){
     const index = tabLabels.indexOf("Confirmed");
    if (index !== -1) {
      tabLabels.splice(index + 1, 0, "Awaiting");
      tabColors.splice(index + 1, 0, "#0066ff");
    }
  }

   // Insert Lost tab before completed if status is LOST
  if(showLost){
     const index = tabLabels.indexOf("Job");
    if (index !== -1) {
      tabLabels.splice(index + 1, 0, "Lost");
      tabColors.splice(index + 1, 0, "#f6b26b");
    }
  }

     // Insert Dispute tab before completed if status is DISPUTE
  if(showDispute){
     const index = tabLabels.indexOf("Job");
    if (index !== -1) {
      tabLabels.splice(index + 1, 0, "Dispute");
      tabColors.splice(index + 1, 0, "#000000");
    }
  }

  // Update value if the current status maps to a newly inserted tab index
  useEffect(() => {
    // If status is STORAGE flow, map selected tab accordingly
    if (isStorageFlow) {
      setValue(showStorage ? 1 : 0);
      return;
    }
 
    if(isDebtorFlow){
      setValue(0);
      return;
    }

    if(isExternalFlow){
      setValue(0);
      return;
    }

    // recalculate statusToIndex keys with provisional and archived inserted
    let newStatusToIndex: { [key: string]: number } = {
      NEW: 0,
      ESTIMATE: 1,
      ESTIMATES: 1,
      SURVEY: 2,
      QUOTE: 3,
      CONFIRMED: 4,
      JOB: 5,
      COMPLETED: 6,
    };

    if (isStorageFlow) {
      newStatusToIndex["STORAGE ENQ"] = 0;
      newStatusToIndex.STORAGE_ENQ = 0;
      newStatusToIndex.STORAGE = 1;
    }

    if(isDebtorFlow){
      newStatusToIndex.DEBTOR = 0;
    }

    if(isExternalFlow){
      newStatusToIndex.EXTERNAL = 0;
    }

    if (showProvisional) {
      // Provisional inserted after Confirmed (index 5)
      newStatusToIndex.PROVISIONAL = 4;
      // shift JOB and COMPLETED by 1
      newStatusToIndex.CONFIRMED = 5;
      newStatusToIndex.JOB = 6;
      newStatusToIndex.COMPLETED = 7;
      if (showArchived) {
        newStatusToIndex.ARCHIVED = 7;
        newStatusToIndex.COMPLETED = 8;
      }
    } else if (showArchived) {
      newStatusToIndex.ARCHIVED = 6;
      newStatusToIndex.COMPLETED = 7;
    } else if(showAwaiting){
      newStatusToIndex.AWAITING = 5;
      newStatusToIndex.COMPLETED = 6;
    }else if(showLost){
      newStatusToIndex.LOST = 6;
      newStatusToIndex.COMPLETED = 7;
    }else if(showDispute){
      newStatusToIndex.DISPUTE = 6;
      newStatusToIndex.COMPLETED = 7;
    }

    setValue(newStatusToIndex[normalizedStatus] ?? 0);
  }, [normalizedStatus, showProvisional, showArchived, isStorageFlow,isDebtorFlow,isExternalFlow, showStorage, showAwaiting, showDispute, showLost]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

const getLeadFlag = () => {
  const followUpDate = leadDetails?.lead?.follow_up_date;
  const createdDate = leadDetails?.lead?.created_at;

  if (!followUpDate) return null;

  const today = new Date();
  const followUp = new Date(followUpDate);
  const created = createdDate ? new Date(createdDate) : today;

  const diffDays = Math.ceil(
    (followUp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  const leadAge = Math.ceil(
    (today.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays === 0) return "Pending Today";

  if (diffDays > 0 && diffDays <= 7) return "Contact Soon";

  if (diffDays < 0 && leadAge < 30) return "Overdue";

  if (leadAge >= 30) return "Old";

  return "Pending";
};

const leadFlag = getLeadFlag();

  return (
    <>
      <GradientTabs
        sx={{ padding: "6px 0px 6px 8px", borderTop: "1px solid #C4CDD5", position: 'relative', zIndex: 999 }}
        value={value}
        // onChange={handleChange}
        aria-label="styled tabs example"
      >
        {tabColors.map((color, index) => (
          <GradientTab
            key={index}
            color={hasStatus ? color : "#7f7f7f"}
            isFirstTab={index === 0}
            isLastTab={index === tabColors.length - 1}
            tabStatus={
              index < value ? "before" : index === value ? "selected" : "after"
            }
            statusLabel={tabLabels[index]}
            disabled={!hasStatus}
            // ✅ Custom label with optional Flag icon on New Lead tab
            label={
              <Box
                sx={{
                  display: "block",
                  flexDirection:'row',
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  position: "relative",
                }}
              >
              {index === 0 && (normalizedStatus === "NEW" || normalizedStatus === "NEW LEAD") && leadFlag && ( <Chip
                  label="Pending"
                  size="small"
                  sx={{
                    height: "14px",
                    fontSize: "8px",
                    fontWeight: 700,
                    backgroundColor: "#fff",
                    color: color,
                    border: `1px solid ${color}`,
                    letterSpacing: "0.5px",
                    "& .MuiChip-label": {
                      padding: "0 4px",
                    },
                  }}
                />
                )}
                <span >{tabLabels[index]}</span>
              </Box>
            }
          />
        ))}
      </GradientTabs>
    </>
  );
};

export default RemovalTab;
